/**
 * Generic Text Rotator Module
 * Handles fade rotation for any text elements
 */

export interface TextRotatorOptions {
  interval?: number;
  itemSelector?: string;
  activeClass?: string;
  inactiveClass?: string;
}

export class TextRotator {
  private containers: NodeListOf<HTMLElement>;
  private interval: number;
  private itemSelector: string;
  private activeClass: string;
  private inactiveClass: string;
  private timers: Map<HTMLElement, number> = new Map();

  constructor(
    selector: string,
    options: TextRotatorOptions = {}
  ) {
    this.interval = options.interval || 3000;
    this.itemSelector = options.itemSelector || '.rotating-item';
    this.activeClass = options.activeClass || 'opacity-100';
    this.inactiveClass = options.inactiveClass || 'opacity-0';
    
    this.containers = document.querySelectorAll(selector);
    this.init();
  }

  protected init(): void {
    this.containers.forEach((container) => {
      const items = container.querySelectorAll(this.itemSelector);
      if (items.length <= 1) return;

      let currentIndex = 0;

      // Start rotation
      const timer = window.setInterval(() => {
        this.rotateItems(container, items, currentIndex);
        currentIndex = (currentIndex + 1) % items.length;
      }, this.interval);

      // Store timer reference for cleanup
      this.timers.set(container, timer);
    });
  }

  protected rotateItems(
    container: HTMLElement,
    items: NodeListOf<Element>,
    currentIndex: number
  ): void {
    const nextIndex = (currentIndex + 1) % items.length;
    
    // Hide current item
    items[currentIndex].classList.remove(this.activeClass);
    items[currentIndex].classList.add(this.inactiveClass);

    // Show next item
    items[nextIndex].classList.remove(this.inactiveClass);
    items[nextIndex].classList.add(this.activeClass);
  }

  public destroy(): void {
    // Clean up all timers
    this.timers.forEach((timer) => {
      clearInterval(timer);
    });
    this.timers.clear();
  }
}

// Helper function for WordPress block editor
export function initTextRotator(
  selector: string,
  options?: TextRotatorOptions
): TextRotator | null {
  const elements = document.querySelectorAll(selector);
  if (elements.length === 0) return null;
  
  return new TextRotator(selector, options);
}